import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faPen, faTrash, faTrashArrowUp } from '@fortawesome/pro-light-svg-icons';
import { Actions, ofType } from '@ngrx/effects';
import { GridComponent } from '@progress/kendo-angular-grid';
import { cloneDeep } from 'lodash-es';
import { map, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { listAnimation } from '../../../core/animations/animations';
import { ClientModel } from '../../../core/models/client/client.model';
import { ShiveRoutes } from '../../../core/routing/segments/route-segments';
import { ClientsHttpService } from '../../../core/state/clients/clients-http.service';
import * as fromClientActions from '../../../core/state/clients/clients.actions';
import { ShiveGridWrapperComponent } from '../../../shared/components/shive-controls/shive-grid/shive-grid-wrapper.component';
import { DropdownOption } from '../../../shared/data-types/dropdown-option';
import { ProjectStatusFilter } from '../../project/shared/project-detail-types';
import { ClientStatusFilter } from '../client-types';

@Component({
  selector: 'app-list-client',
  templateUrl: './client-list.component.html',
  animations: [listAnimation],
  standalone: false,
})
export class ClientListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('grid') grid: GridComponent;
  @ViewChild('gridWrapperComponent') gridWrapperComponent: ShiveGridWrapperComponent;
  public ShiveRoutes = ShiveRoutes;
  public readonly ClientStatusFilter = ClientStatusFilter;
  public readonly clientStatusFilter: readonly DropdownOption[] = [
    { text: 'Alle', value: ProjectStatusFilter.All },
    { text: 'Aktiv', value: ProjectStatusFilter.Active },
    { text: 'Archiviert', value: ProjectStatusFilter.Archived },
  ];
  public gridData: ClientModel[] = [];

  private readonly subs = new SubSink();
  private readonly actions$ = inject(Actions);
  private currentFilter: ClientStatusFilter = undefined;

  constructor(
    private readonly clientsHttpService: ClientsHttpService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.fetchClients();
    this.httpUpdateCallback();
  }

  ngAfterViewInit(): void {
    this.initContextMenuCallbacks();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public onCellClick(e): void {
    if (e.columnIndex === this.grid.columns.length - 1) {
      return;
    }
    const clientModel = e.dataItem as ClientModel;
    void this.router.navigate(['../', ShiveRoutes.Edit, clientModel.id], {
      relativeTo: this.route,
    });
  }

  public clientStatusFilterChanged(filterOption: DropdownOption): void {
    this.currentFilter = filterOption.value as ClientStatusFilter;
    this.fetchClients();
  }

  private initContextMenuCallbacks() {
    this.gridWrapperComponent.contextMenuItems = [
      {
        text: 'Bearbeiten',
        faIcon: faPen,
        callback: () => {},
        isVisible: (clientModel: ClientModel) => !clientModel.isArchived,
        isActivated: true,
      },
      {
        text: 'Archivieren',
        faIcon: faTrash,
        callback: (clientModel: ClientModel) => {
          const updatedClientModel = cloneDeep(clientModel);
          updatedClientModel.isArchived = true;
          this.clientsHttpService.updateClient(updatedClientModel);
        },
        isVisible: (clientModel: ClientModel) => !clientModel.isArchived,
        isActivated: true,
      },
      {
        text: 'Wiederherstellen',
        faIcon: faTrashArrowUp,
        callback: (clientModel: ClientModel) => {
          const updatedClientModel = cloneDeep(clientModel);
          updatedClientModel.isArchived = false;
          this.clientsHttpService.updateClient(updatedClientModel);
        },
        isVisible: (clientModel: ClientModel) => clientModel.isArchived,
        isActivated: true,
      },
    ];
  }

  private fetchClients() {
    this.clientsHttpService
      .getClients(this.currentFilter)
      .pipe(take(1))
      .subscribe((clients) => (this.gridData = clients));
  }

  private httpUpdateCallback(): void {
    this.actions$
      .pipe(
        ofType(fromClientActions.clientUpdated),
        map((entry) => entry.payload[0]),
      )
      .subscribe(() => {
        this.fetchClients();
      });
  }
}
